import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export const OptionalBlock = Mark.create({
    name: 'optionalBlock',
    parseHTML() {
        return [
            {
                tag: 'span[data-type="optional-block"]',
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'optional-block',
                class: 'optional-block',
            }),
            0,
        ];
    },
    addProseMirrorPlugins() {
        const pluginKey = new PluginKey('optionalBlockBraces');
        return [
            new Plugin({
                key: pluginKey,
                props: {
                    decorations(state) {
                        const { doc } = state;
                        const decorations = [];
                        let currentRange = null;

                        doc.descendants((node, pos) => {
                            if (!node.isInline) {
                                if (currentRange) {
                                    decorations.push(...createOptionalDecorations(currentRange));
                                    currentRange = null;
                                }
                                return true;
                            }

                            const mark = node.marks?.find((m) => m.type.name === 'optionalBlock');

                            if (mark) {
                                if (currentRange) {
                                    currentRange.to = pos + node.nodeSize;
                                } else {
                                    currentRange = {
                                        from: pos,
                                        to: pos + node.nodeSize,
                                    };
                                }
                            } else {
                                if (currentRange) {
                                    decorations.push(...createOptionalDecorations(currentRange));
                                    currentRange = null;
                                }
                            }
                            return false;
                        });

                        if (currentRange) {
                            decorations.push(...createOptionalDecorations(currentRange));
                        }

                        return DecorationSet.create(doc, decorations);
                    },
                },
            }),
        ];
    },
});

function createOptionalDecorations(range) {
    const startWidget = Decoration.widget(
        range.from,
        (view) => {
            const span = document.createElement('span');
            span.className = 'optional-brace-widget optional-brace-start';
            span.setAttribute('contenteditable', 'false');
            span.textContent = '{ ';
            span.title = 'Optional Block (Click to remove)';
            span.style.cursor = 'pointer';
            span.style.userSelect = 'none';
            span.style.webkitUserSelect = 'none';
            span.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                const state = view.state;
                const markType = state.schema.marks.optionalBlock;
                if (markType) {
                    const tr = state.tr;
                    state.doc.descendants((node, pos) => {
                        if (node.isInline && pos >= range.from && (pos + node.nodeSize) <= range.to) {
                            const mark = node.marks?.find(m => m.type === markType);
                            if (mark) {
                                tr.removeMark(pos, pos + node.nodeSize, markType);
                            }
                        }
                    });
                    tr.removeMark(range.from, range.to, markType);
                    view.dispatch(tr);
                }
            };
            return span;
        },
        { side: -1, stopEvent: () => false, ignoreSelection: true, key: `opt-start-${range.from}` }
    );

    const endWidget = Decoration.widget(
        range.to,
        (view) => {
            const span = document.createElement('span');
            span.className = 'optional-brace-widget optional-brace-end';
            span.setAttribute('contenteditable', 'false');
            span.textContent = ' }';
            span.title = 'Optional Block (Click to remove)';
            span.style.cursor = 'pointer';
            span.style.userSelect = 'none';
            span.style.webkitUserSelect = 'none';
            span.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                const state = view.state;
                const markType = state.schema.marks.optionalBlock;
                if (markType) {
                    const tr = state.tr;
                    state.doc.descendants((node, pos) => {
                        if (node.isInline && pos >= range.from && (pos + node.nodeSize) <= range.to) {
                            const mark = node.marks?.find(m => m.type === markType);
                            if (mark) {
                                tr.removeMark(pos, pos + node.nodeSize, markType);
                            }
                        }
                    });
                    tr.removeMark(range.from, range.to, markType);
                    view.dispatch(tr);
                }
            };
            return span;
        },
        { side: 1, stopEvent: () => false, ignoreSelection: true, key: `opt-end-${range.to}` }
    );

    return [startWidget, endWidget];
}
